/** Constants shared by the API and the web app. */
export const APP_NAME = 'Metaclaude';
export const APP_VERSION = '0.95.0';
/**
 * How long a machine token may live. A year is the outer bound, not a default.
 *
 * Here rather than beside its schema in `api-contracts.ts`: the form that mints
 * a token needs the *value* at runtime, and a value import would pull that
 * whole module — every API-only Zod schema in it — into the web app's runtime
 * graph. Types are free to cross; values are not.
 */
export const MAX_API_TOKEN_DAYS = 365;
/** Name of the httpOnly session cookie. Never readable from JavaScript. */
export const SESSION_COOKIE = 'mc_session';
/**
 * Name of the CSRF cookie. Deliberately *not* httpOnly: the double-submit
 * pattern requires the client to read this value and echo it in a header, which
 * a cross-origin attacker cannot do. It carries no authority on its own — only
 * the pairing of this token with the httpOnly session cookie authorises a
 * mutating request.
 */
export const CSRF_COOKIE = 'mc_csrf';
/** Header carrying the double-submit CSRF token on mutating requests. */
export const CSRF_HEADER = 'x-metaclaude-csrf';
/** Maximum bytes we will read into the browser file editor. */
export const MAX_EDITABLE_FILE_BYTES = 2 * 1024 * 1024;
/** Tool results larger than this are truncated for the transcript. */
export const MAX_TOOL_RESULT_CHARS = 24_000;
/** How long an unanswered permission prompt stays valid. */
export const APPROVAL_TIMEOUT_MS = 10 * 60 * 1000;
/** Dimensionality of the built-in hashing embedder. */
export const HASH_EMBEDDING_DIM = 512;
/**
 * Tools that mutate state or reach the network. Used to compute the risk badge
 * shown on a permission prompt and to pick a safe default in the UI.
 */
export const HIGH_RISK_TOOLS = ['Bash', 'Write', 'Edit', 'NotebookEdit', 'KillShell'];
export const NETWORK_TOOLS = ['WebFetch', 'WebSearch'];
export const READ_ONLY_TOOLS = ['Read', 'Glob', 'Grep', 'TodoWrite', 'Task'];
/**
 * The built-in tools a workspace may pre-approve, in the order the settings
 * form offers them.
 *
 * Derived rather than restated: a tool that can open a prompt is exactly a
 * network tool or a high-risk one, and read-only tools are absent because they
 * never prompt — a switch for them would do nothing at all. Network first,
 * because reaching the web is both the mildest of these and the one an
 * unattended run actually needs.
 */
export const PREAPPROVABLE_TOOLS = [...NETWORK_TOOLS, ...HIGH_RISK_TOOLS];
/** The longest a tool name may be. Bounds the stored list; nothing is near it. */
export const MAX_TOOL_NAME_LENGTH = 128;
/**
 * An MCP tool call arrives as `mcp__<server>__<tool>`; a built-in arrives bare.
 *
 * The regex is non-greedy on purpose. Its predecessor was `/^mcp__[^_]+__/`,
 * copied into six places, and it stops at the first underscore — so a server
 * an operator named `my_server` was never stripped, and the risk badge, the
 * transcript card and the grant key all fell through to their default branch
 * on every one of its tools. `McpServerRecord.name` allows underscores, so the
 * name that breaks it is one the form accepts.
 *
 * `mcp__a__b__c` stays ambiguous — the convention cannot say whether the
 * server is `a` or `a__b` — and the shortest wins, which is the reading that
 * gets the common case right.
 */
const MCP_TOOL = /^mcp__(.+?)__(.+)$/;
/** A tool call split into the server that offers it and its own name. */
export function splitToolName(name) {
    const match = MCP_TOOL.exec(name);
    if (!match)
        return { server: null, bare: name };
    return { server: match[1], bare: match[2] };
}
/** The tool's own name, with any MCP server prefix removed. */
export function bareToolName(name) {
    return splitToolName(name).bare;
}
/**
 * The name the CLI reports for a tool an MCP server offers.
 *
 * Beside the parser rather than at the call site, and for the reason the note
 * above `MCP_TOOL` gives: the prefix was spelled by hand in six places once
 * already, and every one of them was wrong about a server named `my_server`.
 * A pre-approval list stores exactly this string, so a builder that disagreed
 * with the parser would produce entries that never match a call.
 */
export function mcpToolName(server, tool) {
    return `mcp__${server}__${tool}`;
}
/**
 * Partition a list of tool names into the ones a workspace may store and the
 * ones it may not.
 *
 * Never throws: the supervisor calls it on every run and must not fail a run
 * over a stale setting — it drops the entry and logs it, exactly as
 * `reviewAdditionalDirectories` does for a directory that has become invalid.
 *
 * The parenthesis rule is the one that is not obvious, and it was measured
 * rather than assumed. A scoped rule — `WebFetch(domain:example.com)` — handed
 * to the CLI on this channel, under the managed policy locks Metaclaude sets,
 * does not scope anything: a fetch of *nodejs.org* went through. So an
 * operator writing one in order to narrow an approval would silently get the
 * whole tool instead. Refusing it is the only honest answer; a rule that
 * quietly means more than it says is worse than no rule.
 */
export function reviewToolNames(names) {
    const allowed = [];
    const rejected = [];
    const seen = new Set();
    for (const raw of names) {
        const name = raw.trim();
        if (!name) {
            rejected.push({ name: raw, reason: 'is empty' });
            continue;
        }
        if (name.length > MAX_TOOL_NAME_LENGTH) {
            rejected.push({ name, reason: `is longer than ${MAX_TOOL_NAME_LENGTH} characters` });
            continue;
        }
        if (name.includes('(') || name.includes(')')) {
            rejected.push({ name, reason: 'is a scoped rule, and the CLI widens it to the whole tool' });
            continue;
        }
        if (!/^[A-Za-z0-9_.-]+$/.test(name)) {
            rejected.push({ name, reason: 'is not a tool name' });
            continue;
        }
        if (seen.has(name))
            continue;
        seen.add(name);
        allowed.push(name);
    }
    return { allowed, rejected };
}
/* -------------------------------------------------------------------------- */
/* The CLI's own tools                                                         */
/* -------------------------------------------------------------------------- */
/**
 * The tool that loads the other tools.
 *
 * Not a capability, which is why it is the one name a deny list refuses. The
 * CLI keeps every MCP tool schema — and most of its own — *out* of the prompt
 * and hands them over on demand through this; denying it turns that off with
 * no other symptom than the bill. Measured against CLI 2.1.267 on the same
 * workspace, one turn each: with it, `System tools` occupies 23,543 tokens and
 * the MCP schemas sit outside the window entirely; without it, `System tools`
 * is 39,045 and the MCP tools are loaded. Fifteen and a half thousand tokens
 * on every run, for a checkbox that reads like turning off a search feature.
 */
export const TOOL_SEARCH_TOOL = 'ToolSearch';
/**
 * The CLI tools a Metaclaude deployment starts with switched off.
 *
 * Claude Code brings its own tools, and it is written for a person at a
 * terminal signed in to claude.ai. Several of those describe capabilities this
 * deployment does not have, and three of them reach past it: `CronCreate` and
 * its pair let a run schedule work in the CLI's own scheduler — outside the
 * automations screen, outside their quota guard and outside anything an
 * operator can see or stop — and `Artifact` publishes a page to claude.ai from
 * inside a run. `RemoteTrigger`, `PushNotification`, `DesignSync` and the
 * worktree pair are terminal and claude.ai machinery with no meaning here.
 *
 * Measured against CLI 2.1.267: denying exactly these nine takes the in-window
 * `System tools` figure from 23,543 tokens to 13,388, and the deferred half
 * from 15,378 to 8,047. Ten thousand tokens off the cached prefix of every
 * run, and three doors closed.
 *
 * `MAX_DISABLED_CLI_TOOLS` above bounds what may be stored: the CLI offers
 * around thirty built-ins, so a hundred is far past any real answer and is
 * there to bound a hand-made request rather than to shape a form.
 *
 * A default, not a rule — `CliToolPolicy` stores whatever the operator sets,
 * and the System screen lists what the CLI actually offers rather than this.
 * A name here that a future CLI drops is inert, which is why the list may name
 * tools this platform does not have.
 */
export const MAX_DISABLED_CLI_TOOLS = 100;
export const DEFAULT_DISABLED_CLI_TOOLS = [
    'Artifact',
    'CronCreate',
    'CronDelete',
    'CronList',
    'DesignSync',
    'EnterWorktree',
    'ExitWorktree',
    'PushNotification',
    'RemoteTrigger',
];
/**
 * A ceiling on how many of the CLI's own skills a deployment may name.
 *
 * Seventeen ship today. A hundred bounds a hand-made request without being a
 * number anyone will meet.
 */
export const MAX_CLI_SKILLS = 100;
/**
 * Vet a list of tool names that are to be *refused*.
 *
 * `reviewToolNames`' rules and one more, which only applies in this direction:
 * pre-approving `ToolSearch` is harmless, and denying it is a fifteen-thousand
 * token regression with no visible symptom. So the extra rule lives here
 * rather than in the shared vetting, and both deny lists — a workspace's own
 * and the deployment's — go through it.
 */
export function reviewDeniedToolNames(names) {
    const review = reviewToolNames(names);
    const allowed = [];
    const rejected = [...review.rejected];
    for (const name of review.allowed) {
        if (name === TOOL_SEARCH_TOOL) {
            rejected.push({
                name,
                reason: 'is how the CLI loads every other tool on demand, so refusing it puts them all back into every prompt',
            });
            continue;
        }
        allowed.push(name);
    }
    return { allowed, rejected };
}
/**
 * Whether a tool call is covered by a workspace's pre-approval list.
 *
 * Exact names only, and that is deliberate: a bare `search` in the list must
 * not reach `mcp__some-server__search`. The operator approved a tool they
 * could name, not every tool that happens to end the same way.
 */
export function isPreapprovedTool(preapproved, toolName) {
    return preapproved.some((entry) => entry.trim() === toolName);
}
/**
 * Bash command fragments that always escalate a prompt to `high` risk even when
 * the tool itself would be medium. These are matched case-insensitively against
 * the command string.
 */
export const DANGEROUS_COMMAND_PATTERNS = [
    /\brm\s+(-[a-z]*\s+)*-[a-z]*[rf]/i,
    /\bmkfs\b/i,
    /\bdd\s+if=/i,
    /:\(\)\s*\{.*\}\s*;\s*:/, // fork bomb
    /\bchmod\s+(-[a-zA-Z]+\s+)*777\b/i,
    /\bcurl\b[^|]*\|\s*(ba)?sh/i,
    /\bwget\b[^|]*\|\s*(ba)?sh/i,
    /\bgit\s+push\b.*--force\b/i,
    /\bshutdown\b|\breboot\b/i,
    /\bsudo\b/i,
    />\s*\/dev\/sd[a-z]/i,
];
/** Language inference for the editor and for syntax highlighting. */
export const EXTENSION_LANGUAGE = {
    ts: 'typescript',
    tsx: 'tsx',
    js: 'javascript',
    jsx: 'jsx',
    mjs: 'javascript',
    cjs: 'javascript',
    json: 'json',
    jsonc: 'json',
    md: 'markdown',
    mdx: 'markdown',
    py: 'python',
    rs: 'rust',
    go: 'go',
    java: 'java',
    kt: 'kotlin',
    rb: 'ruby',
    php: 'php',
    c: 'c',
    h: 'c',
    cpp: 'cpp',
    hpp: 'cpp',
    cs: 'csharp',
    swift: 'swift',
    sh: 'shell',
    bash: 'shell',
    zsh: 'shell',
    fish: 'shell',
    yml: 'yaml',
    yaml: 'yaml',
    toml: 'toml',
    ini: 'ini',
    sql: 'sql',
    html: 'html',
    htm: 'html',
    css: 'css',
    scss: 'scss',
    less: 'less',
    vue: 'vue',
    svelte: 'svelte',
    dockerfile: 'dockerfile',
    graphql: 'graphql',
    proto: 'protobuf',
    xml: 'xml',
    svg: 'xml',
    txt: 'text',
    env: 'shell',
};
export function languageForPath(path) {
    const base = path.slice(path.lastIndexOf('/') + 1).toLowerCase();
    if (base === 'dockerfile' || base.startsWith('dockerfile.'))
        return 'dockerfile';
    if (base === 'makefile')
        return 'makefile';
    if (base.startsWith('.env'))
        return 'shell';
    const dot = base.lastIndexOf('.');
    if (dot < 0)
        return null;
    return EXTENSION_LANGUAGE[base.slice(dot + 1)] ?? null;
}
/* -------------------------------------------------------------------------- */
/* The two built-in tools that reach an extension                              */
/* -------------------------------------------------------------------------- */
/**
 * The tool the CLI calls to open a skill, and the field naming which one.
 *
 * Measured against Claude Code through the SDK on 2026-09-10, because the SDK
 * declares no input type for it — `ToolInputSchemas` in `sdk-tools.d.ts` has an
 * entry for every other built-in and none for this. A run told to use a skill
 * emits `{"skill":"probe-widget"}`, the directory name, and nothing else.
 */
export const SKILL_TOOL = 'Skill';
export const SKILL_TOOL_FIELD = 'skill';
/**
 * The tool the CLI calls to delegate, and the field naming the subagent.
 *
 * It is `Agent`, not `Task`, and that had been wrong here in three places: the
 * permission card's summary, the transcript's tool label, and a comment in
 * `context.ts` reasoning about which tools only read. Measured twice on
 * 2026-09-10 — once from this harness and once with every `CLAUDE_CODE_*`
 * variable stripped, because a difference observed once identifies a
 * difference and never its cause — and the name was `Agent` both times, with
 * `{description, subagent_type, prompt}` as its input. The SDK agrees: the
 * union declares `AgentInput`, and there has never been a `TaskInput`.
 *
 * `Task` is kept as an accepted alias rather than deleted. Nothing here can
 * see which name a *future* CLI uses, the cost of accepting both is one array
 * entry, and the cost of being wrong is an invocation nobody counts.
 */
export const DELEGATION_TOOLS = ['Agent', 'Task'];
export const DELEGATION_TOOL_FIELD = 'subagent_type';
/** Whether a tool call is a delegation to a named subagent. */
export function isDelegationTool(name) {
    return DELEGATION_TOOLS.includes(name);
}
//# sourceMappingURL=constants.js.map